import { useState } from 'react'
import { useApolloClient } from '@apollo/react-hooks'
import { useSnackbar } from 'notistack'
import { getFeature } from '@paintscout/util/builder'
import type { UploadOptions } from '@paintscout/util/s3'
import { compressFile, uploadFile } from '@paintscout/util/s3'

import { useClientOptions } from '../../providers/ClientOptionsProvider'

export interface ImageUploadResult {
  src: string
  cloudinaryPublicId: string
  s3PublicKey: string
  format: string
  pages: number
  width: number
  height: number
  type: string
}

export interface UseImageUploadArgs {
  maxWidth?: number
  maxHeight?: number
  uploadOptions?: UploadOptions
  onUpload: (args: ImageUploadResult) => any
  /**
   * If provided, a dropped file is handed off to this callback and NOT uploaded.
   * (Used by the core UploadImage's deferred-upload flow.)
   */
  onFileSelected?: (file: File) => void
  /**
   * Message shown in the error snackbar. Callers that translate should pass a
   * pre-translated string.
   */
  errorMessage?: string
  /**
   * Override the company/feature-flag used for the upload. When omitted, both fall
   * back to the values from {@link useClientOptions} (the ambient ClientOptionsProvider).
   */
  companyId?: string
  s3UploadFeatureEnabled?: boolean
  /**
   * LaunchDarkly `s3MediaUpload` flag value. When true, PDFs/videos are also
   * routed to S3 instead of being force-routed to Cloudinary (see uploadFile.ts).
   */
  s3MediaUploadEnabled?: boolean
}

export interface UseImageUploadResult {
  loading: boolean
  handleDrop: (files: File[]) => Promise<void>
}

/**
 * Shared upload orchestration for the core and presentation UploadImage components (#21305):
 * compress → upload → onUpload, loading state, error snackbar, and provider fallback for the
 * company/feature-flag. Keeping this in one place removes the duplicated, upload-sensitive
 * logic while letting each component render its own preview.
 */
export function useImageUpload({
  maxWidth,
  maxHeight,
  uploadOptions,
  onUpload,
  onFileSelected,
  errorMessage = 'Unable to upload image',
  companyId,
  s3UploadFeatureEnabled,
  s3MediaUploadEnabled
}: UseImageUploadArgs): UseImageUploadResult {
  const { enqueueSnackbar } = useSnackbar()
  const apolloClient = useApolloClient()
  const { options, clientId } = useClientOptions()
  const [loading, setLoading] = useState(false)

  const resolvedCompanyId = companyId ?? clientId
  const resolvedS3UploadFeatureEnabled =
    s3UploadFeatureEnabled ?? getFeature({ options, path: 's3ImageUpload.enabled' })

  async function handleDrop(files: File[]) {
    if (onFileSelected) {
      onFileSelected(files[0])
      return
    }
    try {
      setLoading(true)
      const file = await compressFile({ file: files[0], maxHeight, maxWidth })
      const finalUploadOptions = {
        companyId: resolvedCompanyId,
        s3UploadFeatureEnabled: resolvedS3UploadFeatureEnabled,
        s3MediaUploadEnabled,
        ...uploadOptions
      }
      const { format, pages, width, height, type, src, cloudinaryPublicId, s3PublicKey } = await uploadFile({
        apolloClient,
        file,
        uploadOptions: finalUploadOptions
      })

      onUpload({ src, format, pages, cloudinaryPublicId, s3PublicKey, width, height, type })
    } catch (e) {
      console.error(e)
      enqueueSnackbar(errorMessage, { variant: 'error' })
    }
    setLoading(false)
  }

  return { loading, handleDrop }
}
